import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostBinding,
  inject,
  OnInit,
  QueryList,
  Renderer2,
  signal,
  Type,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { of, take } from 'rxjs';
import {
  CmsComponent,
  ComponentConfig,
  ContentSlot,
  MetaDataModel,
  Page,
  PageType,
  SupportedTypeCodes,
} from '@boels/models';
import { componentsConfig } from '../components.config';
import {
  CmsService,
  MetaDataService,
  RoutingService,
  WindowRef,
} from '@boels/helpers';
import {
  ProductFacade,
  SlotDecoratorDirective,
  SmartEditService,
  triggerLoad$,
} from '@boels/shared-state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'boels-product-details-template-renderer',
  imports: [CommonModule, SlotDecoratorDirective],
  templateUrl: './product-details-template-renderer.component.html',
  styleUrls: ['./product-details-template-renderer.component.scss'],
})
export class CurrentProductTemplateRendererComponent implements OnInit {
  /** Injected services */
  #productFacade = inject(ProductFacade);
  #cmsService = inject(CmsService);
  #routeService = inject(RoutingService);
  #metaDataService = inject(MetaDataService);
  #smartEditService = inject(SmartEditService);
  #renderer = inject(Renderer2);
  #winRef = inject(WindowRef);
  #destroyRef = inject(DestroyRef);

  @ViewChildren('slotsRenderer', { read: ViewContainerRef })
  slotsRenderer: QueryList<CurrentProductTemplateRendererComponent>;
  @HostBinding('class') pageTemplate: string | undefined;

  $page = signal<Page | undefined>(
    this.#routeService.getRoutePropertyWithValue<Page, 'data'>(
      'data',
      'routeResolver',
    ),
  );
  $pageSlots = signal<ContentSlot[] | undefined>(undefined);
  $pageFold = signal<ContentSlot | undefined>(undefined);

  ngOnInit(): void {
    triggerLoad$
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#winRef.document.location.reload();
      });

    const metaData = this.#routeService.getRoutePropertyWithValue<
      MetaDataModel,
      'data'
    >('data', 'metaData');

    const page = this.$page();
    if (!page) return;
    this.$pageSlots.set(this.#cmsService.getMainLayoutSlots(page));
    this.getFoldBreakPointSlot(page);
    this.setPageTemplate(page);
    this.createMainComponents(page);

    //If metaTitle or metaDescription od not exist do not set them
    // i.e. the last set value will be shown (the previous page title and description)
    if (metaData?.metaTitle) {
      this.#metaDataService.setTitle(metaData.metaTitle);
    }
    this.#metaDataService.setMetaDescription(metaData?.metaDescription);
  }

  getFoldBreakPointSlot(page: Page): void {
    this.$pageFold.set(this.#cmsService.getFoldBreakpointSlot(page));
  }

  setPageTemplate(page: Page): void {
    this.pageTemplate = page.template;
  }

  createMainComponents(page: Page): void {
    let components = [];
    this.$pageSlots()?.forEach(
      async (slot: ContentSlot | any, index: number) => {
        components = await this.fetchResolvedComponents(page, slot);
        this.renderComponents(page, components, index);
      },
    );
  }

  fetchResolvedComponents(
    page: Page,
    slot: ContentSlot | any,
  ): Promise<{ component: Type<any>; cmsComponent: CmsComponent }[]> {
    return Promise.all(
      slot.components.component.map(async (cmsComponent: CmsComponent) => {
        const component = await this.getComponent(page, cmsComponent);
        return { component, cmsComponent };
      }),
    );
  }

  renderComponents(page: Page, components: any[], slotIndex: number): void {
    components
      ?.filter((component: any) => component.component)
      .forEach((component: any) => {
        const mappedComponentInputs: any = this.getComponentDefaultInputs(
          page,
          component.cmsComponent,
        );
        this.renderDataComponent(
          page,
          component.cmsComponent,
          slotIndex,
          component.component,
          mappedComponentInputs,
        );
      });
  }

  async getComponent(
    page: Page,
    cmsComponent: CmsComponent,
  ): Promise<Type<any>> {
    if (page.typeCode === PageType.PRODUCT_PAGE) {
      return this.findComponent(cmsComponent.uid)?.component();
    } else {
      return this.getComponentType(
        cmsComponent.typeCode &&
          Object.keys(SupportedTypeCodes).includes(cmsComponent.typeCode)
          ? cmsComponent.flexType
          : cmsComponent.typeCode,
      )?.component();
    }
  }

  getComponentDefaultInputs(
    page: Page,
    cmsComponent: CmsComponent,
  ): {
    key: string;
    value: any;
  } | null {
    if (page.typeCode === PageType.PRODUCT_PAGE) {
      return this.findComponent(cmsComponent.uid)?.inputs || null;
    } else {
      return (
        this.getComponentType(
          cmsComponent.typeCode &&
            Object.keys(SupportedTypeCodes).includes(cmsComponent.typeCode)
            ? cmsComponent.flexType
            : cmsComponent.typeCode,
        )?.inputs || null
      );
    }
  }

  createComponent(
    slotIndex: number,
    mappedComponent?: Awaited<Promise<any>>,
  ): any {
    return (
      mappedComponent &&
      this.slotsRenderer.get(slotIndex)?.createComponent(mappedComponent)
    );
  }

  renderDataComponent(
    page: Page,
    cmsComponent: CmsComponent,
    slotIndex: number,
    mappedComponent: Awaited<Promise<any>>,
    mappedComponentInputs?: any,
  ): void {
    const renderedComponent = this.createComponent(slotIndex, mappedComponent);
    if (renderedComponent) {
      if (page.typeCode === PageType.PRODUCT_PAGE) {
        renderedComponent.instance.componentData = {
          data$: of(cmsComponent),
          product$: this.#productFacade.getCurrentProduct().pipe(take(1)),
        };
      } else {
        renderedComponent.instance.componentData = {
          data$: of(cmsComponent),
        };
      }
    }
    if (renderedComponent && cmsComponent.properties) {
      this.#smartEditService.addSmartEditContract(
        renderedComponent.location.nativeElement,
        this.#renderer,
        cmsComponent.properties,
      );
    }

    if (mappedComponentInputs)
      Object.entries(mappedComponentInputs).map(([input, value]) => {
        renderedComponent.setInput(input, value);
      });
  }

  protected getComponentType(typeName?: string): ComponentConfig | undefined {
    return componentsConfig.find(
      (item: ComponentConfig) => item.cmsComponentTypeCode === typeName,
    );
  }
  findComponent(uid?: string): ComponentConfig | undefined {
    return componentsConfig.find((item: ComponentConfig) => item.uid === uid);
  }
}
